import { useCallback } from 'react'
import { CircularProgress, styled } from '@mui/material'
import { VisibilityOffOutlined } from '@mui/icons-material'
import { useConditionalHook } from '@weblite/use-conditional-hook'
import { circularProgressClasses } from '@mui/material/CircularProgress'
import { UserListItemSkeleton } from '$common/user-list-item'
import { RoomAppBarInfo } from '$common/room-app-bar/room-app-bar-info'
import { openHallProfile } from '$logic/router'
import { useHall, useHideMemberCount } from '$logic/hall/react-hook'
import { toLocale } from '$func/text'
import { HallAppBarCallElement } from '$common/room-app-bar/components/hall-app-bar-call-element'
import { t } from '$i18n'
import { HallAvatar } from '$common/avatar'
import { stringToColor } from '$func/string-to-color'
import { useClientUserPermissions } from '$logic/subscription/react-hook'
import hostConf from '$config/frontend'
import { useStartCall } from './react-hooks'
import { ROOM_APP_BAR_AVATAR_SIZE } from '../conf'
import { useWorkspaceColorByRoomId } from '$logic/workspace'
import { useOnboarding } from '$logic/onboarding/react-hooks'
import { CommonOnboarding } from '$logic/onboarding/types'
import { showFeatureOnboardingDialog } from '$comp/modals/feature-onboarding'

const hidePrivateHallMemberCount = hostConf.VITE_HIDE_PRIVATE_HALL_MEMBER_COUNT

const StyledCircularProgress = styled(CircularProgress)`
  color: ${({ theme }) => theme.palette.neutral700};
  margin-left: 4px;
  & .${circularProgressClasses.circle} {
    stroke-linecap: round;
  }
`

export function ChannelAppBar({ roomId }: { roomId: string }) {
  const { startingCall, openChooseCallDialog, call, canStartCall } =
    useStartCall({ roomId, hallType: 'CHANNEL' })

  const { isOwner, isAdmin } = useClientUserPermissions(roomId) || {}
  const { entity: hall } = useHall(roomId)

  const hideMemberCount = useHideMemberCount(roomId)

  const { enabled: onboardingEnabled, completeOnboarding } =
    useConditionalHook(!!isAdmin, {
      hook: useOnboarding,
      hookArgs: CommonOnboarding.CHANNEL_PROFILE,
    }) || {}

  const handleOnboarding = useCallback(() => {
    showFeatureOnboardingDialog({
      feature: CommonOnboarding.CHANNEL_PROFILE,
    }).then(() => {
      completeOnboarding?.()
    })
  }, [completeOnboarding])

  const backgroundColor = useWorkspaceColorByRoomId(roomId)
  if (!hall) return <UserListItemSkeleton />
  const avatarBackgroundColor = stringToColor(hall.name, roomId)

  return (
    <RoomAppBarInfo
      backgroundColor={backgroundColor}
      onClick={() => openHallProfile(hall.roomId)}
      avatarElement={
        <HallAvatar
          size={ROOM_APP_BAR_AVATAR_SIZE}
          roomId={hall.roomId}
          withStory
          handleOnboarding={onboardingEnabled ? handleOnboarding : undefined}
        />
      }
      avatarSrc=""
      primaryText={hall.name}
      secondaryText={
        <>
          {hideMemberCount && isOwner && (
            <VisibilityOffOutlined sx={{ width: '16px', height: '16px' }} />
          )}
          {isOwner &&
            hall?.membersCount &&
            (!hidePrivateHallMemberCount || !hideMemberCount || hall?.public)
            ? `${toLocale(hall.membersCount)} ${t('profile.hall.subscriber')}`
            : ''}
        </>
      }
      verified={hall.verified}
      avatarBackgroundColor={avatarBackgroundColor}
      deleted={!!hall?.deletedAt}
      callElement={
        call || !canStartCall ? null : startingCall ? (
          <StyledCircularProgress size={24} />
        ) : (
          <HallAppBarCallElement
            onClick={openChooseCallDialog}
            roomType="CHANNEL"
            background={backgroundColor}
          />
        )
      }
    />
  )
}
